//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';

import { getTrackingParamsGroupWithDefault } from '@account-settings/redux/trackingParamSettings/selectors/getTrackingParamsGroup';
import { ITrackingParamSettingsState } from '@account-settings/redux/trackingParamSettings/trackingParamSettingsModel';
import * as accountSettingsApi from '@apis/account-settings/accountSettingsApi';
import { ISimpleTrackingParam } from '@apis/account-settings/accountSettingsApiModel';
import { SimpleCache } from '@hooks/useSimpleCache';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import {
  ITrackingParam,
  ITrackingParamsResponse,
} from '@legacy-components/common/universal-image-actions/universal-image-actions-api-service';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isDefined } from '@utils/filterPredicates';

const initialState: ITrackingParamSettingsState = {
  settings: undefined,
  recentTrackingParamsSearchResult: undefined,
  flags: {
    isRequestingSettings: false,
    hasFetchedSettings: false,
    isRequestingCreateCustomParam: false,
    isRequestingEditCustomParam: false,
    isRequestingDeleteCustomParam: false,
    isUpdatingCuralateParams: false,
    isUpdatingGoogleParams: false,
    isSearchingRecentTrackingParams: false,
  },
  errors: {
    requestSettingsError: undefined,
    requestCreateCustomParamError: undefined,
    requestEditCustomParamError: undefined,
    requestDeleteCustomParamError: undefined,
    updateCuralateParamsError: undefined,
    updateGoogleParamsError: undefined,
    requestSearchRecentTrackingParamsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.AccountSettingsTrackingParams,
  initialState,
  reducers: {
    requestSettings(state) {
      state.flags.isRequestingSettings = true;
      state.errors.requestSettingsError = undefined;
    },
    receiveSettingsSucceeded(
      state,
      action: PayloadAction<ITrackingParamsResponse>,
    ) {
      state.settings = action.payload;
      state.flags.isRequestingSettings = false;
      state.flags.hasFetchedSettings = true;
    },
    receiveSettingsFailed(state, action: PayloadAction<string>) {
      state.errors.requestSettingsError = action.payload;
      state.flags.isRequestingSettings = false;
    },
    requestUpdateCuralateParams(state) {
      state.flags.isUpdatingCuralateParams = true;
      state.errors.updateCuralateParamsError = undefined;
    },
    receiveUpdateCuralateParamsSucceeded(state) {
      state.flags.isUpdatingCuralateParams = false;
    },
    receiveUpdateCuralateParamsFailed(state, action: PayloadAction<string>) {
      state.errors.updateCuralateParamsError = action.payload;
      state.flags.isUpdatingCuralateParams = false;
    },
    requestUpdateGoogleParams(state) {
      state.flags.isUpdatingGoogleParams = true;
      state.errors.updateGoogleParamsError = undefined;
    },
    receiveUpdateGoogleParamsSucceeded(state) {
      state.flags.isUpdatingGoogleParams = false;
    },
    receiveUpdateGoogleParamsFailed(state, action: PayloadAction<string>) {
      state.errors.updateGoogleParamsError = action.payload;
      state.flags.isUpdatingGoogleParams = false;
    },
    requestCreateCustomParam(state) {
      state.errors.requestCreateCustomParamError = undefined;
      state.flags.isRequestingCreateCustomParam = true;
    },
    receiveCreateCustomParamSucceeded(state) {
      state.flags.isRequestingCreateCustomParam = false;
    },
    receiveCreateCustomParamFailed(state, action: PayloadAction<string>) {
      state.errors.requestCreateCustomParamError = action.payload;
      state.flags.isRequestingCreateCustomParam = false;
    },
    requestEditCustomParam(state) {
      state.errors.requestEditCustomParamError = undefined;
      state.flags.isRequestingEditCustomParam = true;
    },
    receiveEditCustomParamSucceeded(state) {
      state.flags.isRequestingEditCustomParam = false;
    },
    receiveEditCustomParamFailed(state, action: PayloadAction<string>) {
      state.errors.requestEditCustomParamError = action.payload;
      state.flags.isRequestingEditCustomParam = false;
    },
    requestDeleteCustomParam(state) {
      state.errors.requestDeleteCustomParamError = undefined;
      state.flags.isRequestingDeleteCustomParam = true;
    },
    receiveDeleteCustomParamSucceeded(state) {
      state.flags.isRequestingDeleteCustomParam = false;
    },
    receiveDeleteCustomParamFailed(state, action: PayloadAction<string>) {
      state.errors.requestDeleteCustomParamError = action.payload;
      state.flags.isRequestingDeleteCustomParam = false;
    },
    requestSearchRecentTrackingParams(state) {
      state.errors.requestSearchRecentTrackingParamsError = undefined;
      state.flags.isSearchingRecentTrackingParams = true;
      state.recentTrackingParamsSearchResult = undefined;
    },
    receiveSearchRecentTrackingParamsSucceeded(
      state,
      action: PayloadAction<ISimpleTrackingParam[]>,
    ) {
      state.flags.isSearchingRecentTrackingParams = false;
      state.recentTrackingParamsSearchResult = action.payload;
    },
    receiveSearchRecentTrackingParamsFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.requestSearchRecentTrackingParamsError = action.payload;
      state.flags.isSearchingRecentTrackingParams = false;
    },
  },
});

export const fetchSettings = createThunk(() => async (dispatch) => {
  dispatch(actions.requestSettings());
  try {
    const trackingParamSettings =
      await accountSettingsApi.getTrackingParamSettings();
    dispatch(actions.receiveSettingsSucceeded(trackingParamSettings));
  } catch (_e) {
    dispatch(
      actions.receiveSettingsFailed(
        'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
      ),
    );
  }
});

export const updateGoogleTrackingParams = createThunk(
  (paramValuesByName: PojoMap<string, string>) =>
    async (dispatch, getState) => {
      const settings = getState().accountSettings.trackingParams.settings;
      if (!settings) {
        return;
      }

      dispatch(actions.requestUpdateGoogleParams());

      // Get the google and custom param groups
      const googleParamGroup = getTrackingParamsGroupWithDefault(
        settings,
        'google',
      );
      const customParamsGroup = getTrackingParamsGroupWithDefault(
        settings,
        'custom',
      );

      // Creates a map which records if a param has actually been updated
      let paramWasUpdatedByName = PojoMap.empty<string, boolean>();
      PojoMap.keys(paramValuesByName).forEach((paramName) => {
        paramWasUpdatedByName = PojoMap.set(
          paramWasUpdatedByName,
          paramName,
          false,
        );
      });

      // For each existing param in the google group update to have the new name
      let googleParams = googleParamGroup.params.map(
        (param): ITrackingParam => {
          if (PojoMap.has(paramValuesByName, param.name)) {
            paramWasUpdatedByName = PojoMap.set(
              paramWasUpdatedByName,
              param.name,
              true,
            );
            return {
              ...param,
              value: PojoMap.get(paramValuesByName, param.name) ?? '',
            };
          }
          return param;
        },
      );

      // If a param wasn't updated this means it wasn't a pre-existing google param, add it here
      // This shouldn't happen often but will help get clients out of broken states where they are missing a param
      PojoMap.keys(paramWasUpdatedByName).forEach((paramName) => {
        if (!PojoMap.get(paramWasUpdatedByName, paramName)) {
          googleParams = googleParams.concat({
            name: paramName,
            curalateReserved: false,
            autoSet: false,
            value: PojoMap.get(paramValuesByName, paramName) ?? '',
          });
        }
      });

      // Update the google params group to use the new updates
      const params = {
        paramGroups: [
          {
            ...googleParamGroup,
            params: googleParams,
          },
          customParamsGroup,
        ],
      };

      try {
        await accountSettingsApi.saveTrackingParams(params);
        dispatch(actions.receiveUpdateGoogleParamsSucceeded());
        await dispatch(fetchSettings());
      } catch (_e) {
        dispatch(
          actions.receiveUpdateGoogleParamsFailed(
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },
);

export const updateCuralateParams = createThunk(
  (reservedParam?: string, referralParam?: string) =>
    async (dispatch, getState) => {
      const settings = getState().accountSettings.trackingParams.settings;
      if (!settings) {
        return;
      }

      dispatch(actions.requestUpdateCuralateParams());

      // Get the google and custom param groups
      const googleParamGroup = getTrackingParamsGroupWithDefault(
        settings,
        'google',
      );
      const customParamsGroup = getTrackingParamsGroupWithDefault(
        settings,
        'custom',
      );

      // Clear out the reserve and auto set params
      let googleParams = googleParamGroup.params.map(
        (param): ITrackingParam => {
          if (param.curalateReserved || param.autoSet) {
            return {
              ...param,
              curalateReserved: false,
              autoSet: false,
              value: param.name === 'utm_source' ? 'curalate' : '',
            };
          }
          return param;
        },
      );

      // Go through existing google params and set the appropriate ones to reserve/referral
      // This keeps track of if they were set for the future
      let wasReservedParamSet = false;
      let wasReferralParamSet = false;
      googleParams = googleParams.map((param) => {
        if (param.name === reservedParam) {
          wasReservedParamSet = true;
          return {
            ...param,
            curalateReserved: true,
            autoSet: false,
            value: 'distribution_touchpoint',
          };
        }
        if (param.name === referralParam) {
          wasReferralParamSet = true;
          return {
            ...param,
            curalateReserved: false,
            autoSet: true,
            value: 'distribution_touchpoint',
          };
        }
        return param;
      });

      // If either the referral or reserve param weren't set and should have been then add a new param for them
      if (
        !wasReservedParamSet &&
        !!reservedParam &&
        reservedParam !== 'undefined'
      ) {
        googleParams = googleParams.concat({
          name: reservedParam,
          curalateReserved: true,
          autoSet: false,
          value: 'distribution_touchpoint',
        });
      }
      if (
        !wasReferralParamSet &&
        !!referralParam &&
        referralParam !== 'undefined'
      ) {
        googleParams = googleParams.concat({
          name: referralParam,
          curalateReserved: false,
          autoSet: true,
          value: 'distribution_touchpoint',
        });
      }

      // Update the google params group to use the new updates
      const params = {
        paramGroups: [
          {
            ...googleParamGroup,
            params: googleParams,
          },
          customParamsGroup,
        ],
      };

      try {
        await accountSettingsApi.saveTrackingParams(params);
        dispatch(actions.receiveUpdateCuralateParamsSucceeded());
        await dispatch(fetchSettings());
      } catch (_e) {
        dispatch(
          actions.receiveUpdateCuralateParamsFailed(
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },
);

export const updateGoogleAndCuralateParams = createThunk(
  (
    paramValuesByName: PojoMap<string, string>,
    reservedParam?: string,
    referralParam?: string,
  ) =>
    async (dispatch, getState) => {
      await dispatch(updateCuralateParams(reservedParam, referralParam));
      await dispatch(updateGoogleTrackingParams(paramValuesByName));
      const { updateCuralateParamsError, updateGoogleParamsError } =
        getState().accountSettings.trackingParams.errors;

      if (!updateCuralateParamsError && !updateGoogleParamsError) {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Successfully Updated your Google Analytics Parameters.',
            type: ToastType.SUCCESS,
          }),
        );
      } else {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title:
              'An error occured when updating your Google Analytics Parameters.',
            type: ToastType.ERROR,
          }),
        );
      }
    },
);

export const createCustomParam = createThunk(
  (name: string, value: string) => async (dispatch, getState) => {
    const settings = getState().accountSettings.trackingParams.settings;
    if (!settings) {
      return;
    }

    dispatch(actions.requestCreateCustomParam());

    // Get the google and custom param groups
    const googleParamGroup = getTrackingParamsGroupWithDefault(
      settings,
      'google',
    );
    const customParamsGroup = getTrackingParamsGroupWithDefault(
      settings,
      'custom',
    );

    // Add a new custom param to the end of the existing custom params group
    const params = {
      paramGroups: [
        googleParamGroup,
        {
          ...customParamsGroup,
          inUse: true,
          params: customParamsGroup.params.concat({
            name,
            value,
            autoSet: false,
          }),
        },
      ],
    };

    try {
      await accountSettingsApi.saveTrackingParams(params);
      dispatch(actions.receiveCreateCustomParamSucceeded());
      await dispatch(fetchSettings());
    } catch (_e) {
      dispatch(
        actions.receiveCreateCustomParamFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const editCustomParam = createThunk(
  (oldParam: ITrackingParam, name: string, value: string) =>
    async (dispatch, getState) => {
      const settings = getState().accountSettings.trackingParams.settings;
      if (!settings) {
        return;
      }

      dispatch(actions.requestEditCustomParam());

      // Get the google and custom param groups
      const googleParamGroup = getTrackingParamsGroupWithDefault(
        settings,
        'google',
      );
      const customParamsGroup = getTrackingParamsGroupWithDefault(
        settings,
        'custom',
      );

      // This goes maps the current custom params to only update the one being updated. This is done with a map so they stay in the same order
      const params = {
        paramGroups: [
          googleParamGroup,
          {
            ...customParamsGroup,
            inUse: true,
            params: customParamsGroup.params.map((param) => {
              if (param.name === oldParam.name) {
                return { name, value, autoSet: false };
              }
              return param;
            }),
          },
        ],
      };

      try {
        await accountSettingsApi.saveTrackingParams(params);
        dispatch(actions.receiveEditCustomParamSucceeded());
        await dispatch(fetchSettings());
      } catch (_e) {
        dispatch(
          actions.receiveEditCustomParamFailed(
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },
);

export const deleteCustomParam = createThunk(
  (paramToDelete: ITrackingParam) => async (dispatch, getState) => {
    const settings = getState().accountSettings.trackingParams.settings;
    if (!settings) {
      return;
    }

    dispatch(actions.requestDeleteCustomParam());

    // Get the google and custom param groups
    const googleParamGroup = getTrackingParamsGroupWithDefault(
      settings,
      'google',
    );
    const customParamsGroup = getTrackingParamsGroupWithDefault(
      settings,
      'custom',
    );

    // Filter out the deleted param and update
    const params = {
      paramGroups: [
        googleParamGroup,
        {
          ...customParamsGroup,
          inUse: true,
          params: customParamsGroup.params.filter(
            (param) => param.name !== paramToDelete.name,
          ),
        },
      ],
    };

    try {
      await accountSettingsApi.saveTrackingParams(params);
      dispatch(actions.receiveDeleteCustomParamSucceeded());
      await dispatch(fetchSettings());
    } catch (_e) {
      dispatch(
        actions.receiveDeleteCustomParamFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const searchRecentTrackingParams = createThunk(
  (
    searchTerm: string | undefined,
    paramName: string | undefined,
    shouldFindNames: boolean,
    abortController: AbortController,
    cache: SimpleCache<ISimpleTrackingParam[]>,
  ) =>
    async (dispatch) => {
      dispatch(actions.requestSearchRecentTrackingParams());

      const cacheResult = cache.get(searchTerm ?? '');

      if (isDefined(cacheResult)) {
        dispatch(
          actions.receiveSearchRecentTrackingParamsSucceeded(cacheResult),
        );
        return;
      }

      try {
        const trackingParams = await accountSettingsApi.searchTrackingParams(
          {
            searchString: searchTerm,
            paramName,
            findNames: shouldFindNames,
            limit: 8,
          },
          abortController.signal,
        );

        cache.set(searchTerm ?? '', trackingParams);

        dispatch(
          actions.receiveSearchRecentTrackingParamsSucceeded(trackingParams),
        );
      } catch (_e) {
        dispatch(
          actions.receiveSearchRecentTrackingParamsFailed(
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },
);

export default reducer;
